import React from 'react';
import { PropTypes as PT } from 'prop-types';
import ReactInfinite from 'react-infinite';
import { FormattedMessage } from 'react-intl';
import _get from 'lodash/get';
import Measure from 'react-measure';
import { ReactComponent as PeopleIcon } from '@lifesize/ux-assets/vectors/svgIcons/people.svg';
import Button from 'components/Button/Button';
import Tooltip from 'components/Tooltip/Tooltip';
import ContactListItem from 'components/Contacts/ContactListItem';
import ContactsFilter from 'components/Contacts/ContactsFilter';
import Spinner from 'components/MainSpinner/MainSpinner';
import ContactCard from 'components/Contacts/ContactCard';
import { routes } from 'constants/Application';
import { participantFilterType } from 'constants/ParticipantList';
import {
  MEETINGS_FILTER_OPTION_ALL,
  MEETINGS_FILTER_OPTION_ACTIVE,
  MEETINGS_FILTER_OPTION_MY_MEETINGS,
  REACT_INFINITE_LOAD_BEGIN_OFFSET
} from 'constants/Contact';
import { selectContact } from 'utils/contact-utils';
import classes from 'components/Contacts/Contacts.module.scss';
import RemoteModerationPanel from 'components/Contacts/RemoteModeratorPanel';

const CONTACT_HEIGHT = 50;

class Meetings extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      listContainerHeight: CONTACT_HEIGHT,
      participantFilter: participantFilterType.none
    };

    this.selectedUuid = _get(this.props, 'match.params.selectedUuid');
  }

  componentDidMount() {
    const selectedContact = this.props.selectedContact;
    const contactIsInCurrentList = !!this.props.contacts.find((contact) => contact.uuid === this.selectedUuid);

    if (!this.props.previousPath || routes.meetings !== this.props.previousPath.substr(0, routes.meetings.length)) {
      // 1. initial load of the app (previousPath = null) OR
      // 2. user is navigating from another route: reset filter and load meeting lists
      this.props.handleFilterChange('meetings', MEETINGS_FILTER_OPTION_ALL);
      if (!this.props.previousPath) {
        // If meetings route is initially loaded, then set current path so that previous is not null
        this.props.setCurrentPath(_get(window.location, 'pathname', routes.meetings));
      }
      this.props.loadInitialMeetings();
      this.props.endRouteLoading();
      this.props.loadActiveMeetings();
      this.props.loadMyMeetings();
    } else {
      // user is navigating from within the /meetings route
      if (!contactIsInCurrentList) {
        // only reset the filter if the selectedUuid is not in the current filter's list
        this.props.handleFilterChange('meetings', MEETINGS_FILTER_OPTION_ALL);
      }
    }
    if (selectedContact) {
      this.scrollToContact(this.selectedUuid);
    }
  }

  getFilterOptions = () => [
    {
      key: MEETINGS_FILTER_OPTION_ALL,
      label: <FormattedMessage id="components.meetings.filter.option.all" defaultMessage="All" />,
      dataTestId: 'meetings-filter-all'
    },
    {
      key: MEETINGS_FILTER_OPTION_ACTIVE,
      label: <FormattedMessage id="components.meetings.filter.option.active" defaultMessage="Active" />,
      dataTestId: 'meetings-filter-active'
    },
    {
      key: MEETINGS_FILTER_OPTION_MY_MEETINGS,
      label: <FormattedMessage id="components.meetings.filter.option.myMeetings" defaultMessage="My Meetings" />,
      dataTestId: 'meetings-filter-mine'
    }
  ];

  handleSelectContact = (contact) => {
    this.props.handleSelectContact(contact);
    selectContact(contact, this.props.history.push);
  };

  renderRightPanel = () => {
    const selectedContact = this.props.selectedContact;
    const isChatEnabled = this.props.isChatEnabled(selectedContact);
    const remoteModerationIsActiveForThisMeeting = this.props.remoteModerationIsActiveForThisMeeting(selectedContact);
    if (remoteModerationIsActiveForThisMeeting) {
      return (
        <RemoteModerationPanel
          contact={selectedContact}
          handleRemoteModerate={this.props.handleRemoteModerate}
          participantFilter={this.state.participantFilter}
          selectParticipantFilter={this.selectParticipantFilter}
        />
      );
    }
    if (!!selectedContact) {
      return (
        <div className={classes.rightContainer}>
          <ContactCard
            contact={selectedContact}
            currentUserId={this.props.currentUserUuid}
            currentUserSuperUser={this.props.currentUserSuperUser}
            handleAudioCall={this.props.handleAudioCall}
            handleChat={this.props.handleChat}
            handleDelete={this.props.handleDelete}
            handleEdit={this.props.handleEdit}
            handleRemoteModerate={this.props.handleRemoteModerate}
            handleSchedule={this.props.handleSchedule}
            handleVideoCall={this.props.handleVideoCall}
            isChatEnabled={isChatEnabled}
            remoteModerationIsActive={this.props.remoteModerationIsActive}
            remoteModerationIsActiveForThisMeeting={remoteModerationIsActiveForThisMeeting}
            unselectContact={() => this.props.history.push(routes.meetings)}
            userCanRemoteModerate={this.props.userCanRemoteModerate(selectedContact)}
          />
        </div>
      );
    }
    return null;
  };

  scrollToContact(selectedId) {
    setTimeout(() => {
      const index = this.props.contacts.findIndex((contact) => contact.uuid === selectedId);
      // react-infinite no longer supports the id prop
      const scrollArea = document.getElementsByClassName('infiniteListContainer')[0];

      if (index > -1 && !!scrollArea) {
        scrollArea.scrollTo({
          top: index * CONTACT_HEIGHT,
          left: 0,
          behavior: 'smooth'
        });
      }
    }, 0);
  }

  selectParticipantFilter = (participantFilter) => {
    this.setState({
      participantFilter
    });
  };

  render() {
    const contactsCount = this.props.contacts.length;
    const selectedContact = this.props.selectedContact;

    return (
      <div className={classes.contactsContainer} id="meetingsContainer">
        <div className={classes.leftContainer}>
          <div className={classes.contactsHeader}>
            <header>
              <div className={classes.headerIconContainer}>
                <PeopleIcon data-testid="peopleIcon" />
              </div>
              <h3>
                <FormattedMessage id="components.meetings.header" defaultMessage="Meetings" />
              </h3>
            </header>
            <div className={classes.headerControlsContainer}>
              <div className={classes.filterControls}>
                <div className={classes.filterLabel}>
                  <FormattedMessage id="components.meetings.filterShow" defaultMessage="Show" />
                </div>
                <div>
                  <ContactsFilter
                    handleFilterChange={this.props.handleFilterChange}
                    options={this.getFilterOptions()}
                    selected={this.props.filter}
                    type="meetings"
                  />
                </div>
              </div>
              <div>
                <Button
                  kind="primary"
                  size="small"
                  style={{
                    width: '162px',
                    minWidth: '162px'
                  }}
                  onClick={this.props.handleCreateContact}
                  disabled={this.props.disableMeetingCreation}
                  dataAttrs={{ 'data-testid': 'create-meeting-btn' }}
                >
                  {this.props.disableMeetingCreation && (
                    <Tooltip
                      location={'left'}
                      label={
                        <FormattedMessage
                          id="components.meetings.buttonDisabled"
                          defaultMessage="Meeting creation is disabled for this account."
                        />
                      }
                      style={{
                        textAlign: 'center',
                        shift: 'left',
                        whiteSpace: 'normal',
                        width: '180px',
                        fontStyle: 'normal',
                        color: 'rgba(255, 255, 255, 0.8)',
                        padding: '5px 13px',
                        fontSize: '12px'
                      }}
                    />
                  )}
                  <FormattedMessage id="components.meetings.createMeetingButton" defaultMessage="Create a Meeting" />
                </Button>
              </div>
            </div>
          </div>
          <Measure
            bounds
            onResize={(dimensions) => {
              this.setState({ listContainerHeight: dimensions.bounds.height });
            }}
          >
            {({ measureRef }) => (
              <div className={classes.contactsListContainer} ref={measureRef}>
                {this.props.loading && (
                  <div>
                    <Spinner
                      config={{
                        lines: 13,
                        length: 17,
                        width: 8,
                        radius: 20,
                        color: '#413675',
                        speed: 0.7
                      }}
                    />
                  </div>
                )}
                {contactsCount ? (
                  <ReactInfinite
                    className={`infiniteListContainer ${classes.contactsList}`}
                    containerHeight={this.state.listContainerHeight || CONTACT_HEIGHT}
                    elementHeight={CONTACT_HEIGHT}
                    infiniteLoadBeginEdgeOffset={REACT_INFINITE_LOAD_BEGIN_OFFSET}
                    onInfiniteLoad={this.props.fetchNext}
                  >
                    {this.props.contacts.map((contact, index) => (
                      <ContactListItem
                        className={classes.contactListItem}
                        contact={contact}
                        currentUserSuperUser={this.props.currentUserSuperUser}
                        currentUserUuid={this.props.currentUserUuid}
                        handleAudioCall={this.props.handleAudioCall}
                        handleChat={this.props.handleChat}
                        handleSchedule={this.props.handleSchedule}
                        handleSelectContact={this.handleSelectContact}
                        handleVideoCall={this.props.handleVideoCall}
                        isFirstContact={index === 0}
                        isChatEnabled={this.props.isChatEnabled(contact)}
                        isLastContact={contactsCount === index + 1}
                        key={contact.id}
                        selectedContact={selectedContact}
                        dataTestTag="meetings-contact"
                      />
                    ))}
                  </ReactInfinite>
                ) : (
                  <div className={classes.noContacts}>
                    <FormattedMessage
                      id="components.meetings.noContacts"
                      defaultMessage="There are no matches for your selection."
                    />
                  </div>
                )}
              </div>
            )}
          </Measure>
        </div>
        {this.renderRightPanel()}
      </div>
    );
  }
}

Meetings.propTypes = {
  setCurrentPath: PT.func.isRequired,
  contacts: PT.array.isRequired,
  currentUserSuperUser: PT.bool,
  currentUserUuid: PT.string,
  disableMeetingCreation: PT.bool,
  endRouteLoading: PT.func,
  fetchNext: PT.func,
  filter: PT.string,
  selectedContact: PT.object,
  handleAudioCall: PT.func.isRequired,
  handleChat: PT.func.isRequired,
  handleCreateContact: PT.func.isRequired,
  handleDelete: PT.func.isRequired,
  handleEdit: PT.func.isRequired,
  handleFilterChange: PT.func.isRequired,
  handleRemoteModerate: PT.func,
  handleSchedule: PT.func.isRequired,
  handleSelectContact: PT.func.isRequired,
  handleVideoCall: PT.func.isRequired,
  isChatEnabled: PT.func,
  loadActiveMeetings: PT.func.isRequired,
  loadInitialMeetings: PT.func,
  loadMyMeetings: PT.func.isRequired,
  loading: PT.bool,
  previousPath: PT.string,
  remoteModerationIsActive: PT.bool,
  remoteModerationIsActiveForThisMeeting: PT.func,
  scrollToSelected: PT.bool,
  userCanRemoteModerate: PT.func
};

export default Meetings;
